import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNewGameURL } from './utils';
import ExplainerPopup from './ExplainerPopup';
import SoloSettingsModal from './SoloSettingsModal';
import Leaderboard from './Leaderboard';
import { taglines, pick } from '../lib/copy';

const getSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3) {
        return parts[0];
    }
    return null;
};

// Decorative tiles scattered behind the hero.
const HERO_WORD = [
    { ch: 'S', pts: 1, rot: -8 },
    { ch: 'P', pts: 3, rot: 5 },
    { ch: 'E', pts: 1, rot: -4 },
    { ch: 'L', pts: 1, rot: 7 },
    { ch: 'L', pts: 1, rot: -6 },
];

const FLOATERS = [
    { ch: 'A', top: '12%', left: '8%', rot: -14, delay: '0s', size: 'sm' },
    { ch: 'Z', top: '22%', left: '84%', rot: 12, delay: '0.6s', size: 'md' },
    { ch: 'Q', top: '68%', left: '6%', rot: 9, delay: '1.1s', size: 'md' },
    { ch: 'K', top: '78%', left: '82%', rot: -10, delay: '0.3s', size: 'sm' },
    { ch: 'W', top: '46%', left: '90%', rot: 6, delay: '1.4s', size: 'xs' },
];

const MainPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showPopup, setShowPopup] = useState(false);
    const [poolSize, setPoolSize] = useState(50);
    const [windowSize, setWindowSize] = useState(5);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [tagIndex, setTagIndex] = useState(0);

    const subdomain = getSubdomain();
    const isSolo = location.pathname === '/solo' || subdomain === 'solo';

    const createGame = () => {
        const settings = { solo: { isSolo: isSolo, poolSize: poolSize, windowSize: windowSize } };
        navigate(getNewGameURL(settings));
    };

    useEffect(() => {
        setShowPopup(location.pathname === '/explain');
    }, [location]);

    useEffect(() => {
        const id = setInterval(() => setTagIndex((i) => i + 1), 3200);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="sg-page" style={{ position: 'relative', overflow: 'hidden' }}>
            {/* floating decorative tiles */}
            <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
                {FLOATERS.map((f, i) => (
                    <div
                        key={i}
                        className={`sg-tile sg-tile--${f.size} sg-anim-float`}
                        style={{ position: 'absolute', top: f.top, left: f.left, '--rot': `${f.rot}deg`, transform: `rotate(${f.rot}deg)`, opacity: 0.5, animationDelay: f.delay }}
                    >
                        {f.ch}
                    </div>
                ))}
            </div>

            {isSolo && (
                <button
                    className="sg-iconbtn"
                    onClick={() => setSettingsOpen(true)}
                    aria-label="Solo settings"
                    style={{ position: 'absolute', top: 'calc(14px + var(--safe-top))', right: 16, zIndex: 5 }}
                >
                    <SettingsGlyph />
                </button>
            )}

            <ExplainerPopup open={showPopup} onClose={() => setShowPopup(false)} />

            <SoloSettingsModal
                open={settingsOpen}
                handleClose={() => setSettingsOpen(false)}
                defaultPoolSize={poolSize}
                defaultWindowSize={windowSize}
                handleUpdate={(p, w) => { setPoolSize(p); setWindowSize(w); }}
            />

            <main
                className="sg-center-col"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 18px 56px', position: 'relative', zIndex: 1 }}
            >
                {isSolo && <div className="sg-kicker sg-anim-fadeup" style={{ marginBottom: 10 }}>Solo mode</div>}

                <h1
                    className="sg-wordmark sg-anim-pop"
                    style={{ fontSize: 'clamp(3rem, 16vw, 5rem)', cursor: 'default', marginBottom: 6 }}
                >
                    spellago<span className="sg-wordmark__dot">.</span>
                </h1>

                <p key={tagIndex} className="sg-muted sg-anim-fadeup" style={{ fontWeight: 700, fontSize: '1.05rem', margin: '0 0 28px', minHeight: '1.4em' }}>
                    {pick(taglines, tagIndex)}
                </p>

                <div className="sg-wrap sg-stagger" style={{ gap: '8px', justifyContent: 'center', marginBottom: 36 }}>
                    {HERO_WORD.map((t, i) => (
                        <div
                            key={i}
                            className="sg-tile sg-tile--lg"
                            data-pts={t.pts}
                            style={{ '--rot': `${t.rot}deg`, transform: `rotate(${t.rot}deg)` }}
                        >
                            {t.ch}
                        </div>
                    ))}
                </div>

                <button className="sg-btn sg-btn--primary sg-btn--lg sg-anim-fadeup" onClick={createGame} style={{ animationDelay: '0.3s' }}>
                    ▶ Play new game
                </button>

                <button
                    className="sg-link sg-anim-fadeup"
                    onClick={() => navigate('/explain')}
                    style={{ background: 'none', border: 'none', marginTop: 18, animationDelay: '0.36s' }}
                >
                    How to play
                </button>

                {isSolo && (
                    <div className="sg-anim-fadeup" style={{ width: '100%', marginTop: 40, animationDelay: '0.42s' }}>
                        <Leaderboard />
                    </div>
                )}

                <p className="sg-faint" style={{ fontWeight: 600, fontSize: '0.85rem', marginTop: 40 }}>
                    Suggestions? Issues?{' '}
                    <a className="sg-link" href="mailto:spellago@gmail.com">Say hi</a>
                </p>
            </main>
        </div>
    );
};

// Small inline gear so we don't depend on MUI icons here.
const SettingsGlyph = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3.2" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

export default MainPage;
